import { ServiceStatus } from '../types';
import { parseServerDate } from './datetime';

export const statusColors: Record<ServiceStatus, string> = {
  up: 'bg-status-up',
  down: 'bg-status-down',
  degraded: 'bg-status-degraded',
  pending: 'bg-status-pending',
  maintenance: 'bg-status-maintenance',
};

export const statusTextColors: Record<ServiceStatus, string> = {
  up: 'text-status-up',
  down: 'text-status-down',
  degraded: 'text-status-degraded',
  pending: 'text-status-pending',
  maintenance: 'text-status-maintenance',
};

/**
 * The same five colours as `theme.colors.status`, as literal hex.
 *
 * SVG presentation attributes (a sparkline's `stroke`/`fill`) do not resolve
 * `var(--token)`, so anything drawn into SVG needs real values — the same
 * reason `utils/chartTheme.ts` exists.
 */
export const statusHex: Record<ServiceStatus, string> = {
  up: '#1F9D57',
  down: '#D14437',
  degraded: '#B5771A',
  pending: '#6B8496',
  maintenance: '#2F8FC7',
};

/** Left-edge severity stripe on the service card. */
export const statusBorderColors: Record<ServiceStatus, string> = {
  up: 'border-l-status-up',
  down: 'border-l-status-down',
  degraded: 'border-l-status-degraded',
  pending: 'border-l-status-pending',
  maintenance: 'border-l-status-maintenance',
};

/** "https://api.example.com/health" -> "api.example.com/health" */
/**
 * The target as a person would say it. HTTP drops the scheme; the other
 * types carry a canonical `scheme://` form that reads better as words.
 */
export function displayUrl(url: string, type?: string): string {
  const m = url.match(/^(tcp|dns|ping|push|group):\/\/(.*)$/);
  if (m) {
    const rest = m[2];
    switch (m[1]) {
      case 'tcp': return `TCP ${rest}`;
      case 'dns': {
        const [name, record] = rest.split('/');
        return `DNS ${record ?? 'A'} ${name}`;
      }
      case 'ping': return `ping ${rest}`;
      case 'push': return 'Heartbeat (push)';
      case 'group': return 'Composite of group';
    }
  }
  if (type === 'heartbeat') return 'Heartbeat (push)';
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

export const typeLabels: Record<string, string> = {
  http: 'HTTP',
  tcp: 'TCP',
  dns: 'DNS',
  ping: 'Ping',
  heartbeat: 'Heartbeat',
  group: 'Composite',
};

/** Rough human span for a window expressed in seconds. */
export function formatSpan(seconds: number): string {
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

export const statusLabels: Record<ServiceStatus, string> = {
  up: 'Operational',
  down: 'Down',
  degraded: 'Degraded',
  pending: 'Pending',
  maintenance: 'Maintenance',
};

/**
 * Uptime for display. `null` is "no data" — a new or long-paused service —
 * and must never read as 100 %. `undefined` means the field was not requested.
 */
export function formatUptime(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${value}%`;
}

export function formatDuration(ms: number | null): string {
  if (ms === null) return '-';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function formatRelativeTime(dateStr: string): string {
  const parsed = parseServerDate(dateStr);
  if (!parsed) return 'unknown';

  const diff = Date.now() - parsed.getTime();
  // A clock skew between server and browser must not read as "in the future".
  if (diff < 0) return 'just now';

  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}
